import { useEffect, useRef, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import { enqueueCreateDirectConversation } from '@/store/localStore';
import { LocalPersistenceError } from '@/protocol/errors';
import { useSession } from '@/transport/SessionProvider';
import type { ResolvedUser } from '@/transport/workerProtocol';
import type { Hex } from '@/store/ids';
import styles from '@/components/DeleteDialog.module.css';

export interface NewConversationDialogProps {
  onOpened: (conversationId: Hex) => void;
  onClose: () => void;
}

/**
 * Find someone, then queue a direct conversation for them.
 *
 * Resolution needs the network — it is a query against the server's user
 * directory and there is nothing local to answer it with — but the creation
 * itself goes through the outbox like every other mutation, so a connection
 * that drops between the two steps leaves a conversation the user can already
 * type into.
 */
export function NewConversationDialog({ onOpened, onClose }: NewConversationDialogProps) {
  const { t } = useStrings();
  const { worker, account } = useSession();
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<ResolvedUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose]);

  const find = async () => {
    const trimmed = query.trim();
    if (trimmed.length === 0 || !worker) {
      return;
    }
    setBusy(true);
    setError(null);
    setFound(null);
    try {
      // A leading "+" is the only reliable signal that the user typed a phone
      // number rather than a username, and E.164 requires it, so it is the
      // discriminator rather than a guess based on digits.
      const isPhone = trimmed.startsWith('+');
      const user = await worker.resolveUser(isPhone ? null : trimmed, isPhone ? trimmed : null);
      if (user) {
        setFound(user);
      } else {
        setError(t('newConversation.notFound'));
      }
    } catch {
      setError(t('newConversation.offline'));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (!found || !account) {
      return;
    }
    try {
      const created = await enqueueCreateDirectConversation({
        peerId: found.userId,
        peerTitle: found.displayName,
        originDeviceId: account.deviceId,
      });
      worker?.notifyOutboxChanged();
      onOpened(created.conversationId);
    } catch (caught) {
      setError(
        caught instanceof LocalPersistenceError
          ? t('status.localFailure')
          : t('newConversation.notFound'),
      );
    }
  };

  return (
    <div className={styles.backdrop}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-conversation-title"
      >
        <h2 id="new-conversation-title" className={styles.title}>
          {t('newConversation.title')}
        </h2>
        <p className={styles.description}>{t('newConversation.description')}</p>

        <label htmlFor="new-conversation-query">{t('newConversation.query')}</label>
        <input
          id="new-conversation-query"
          ref={inputRef}
          className={styles.option}
          value={query}
          dir="auto"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              void find();
            }
          }}
        />

        {found ? (
          <p className={styles.optionLabel} dir="auto">
            {found.displayName}
            {found.username === null ? '' : ` (@${found.username})`}
          </p>
        ) : null}

        {error !== null ? (
          <p className={styles.optionHint} role="alert">
            {error}
          </p>
        ) : null}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onClose}>
            {t('newConversation.cancel')}
          </button>
          {found ? (
            <button
              type="button"
              className={styles.confirm}
              onClick={() => {
                void start();
              }}
            >
              {t('newConversation.start')}
            </button>
          ) : (
            <button
              type="button"
              className={styles.confirm}
              disabled={busy}
              onClick={() => {
                void find();
              }}
            >
              {busy ? t('newConversation.finding') : t('newConversation.find')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
